import { useEffect, useMemo, useState, useCallback, Fragment } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Car, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight, Sparkles, Download, RefreshCw, ShieldCheck, Info, Loader2, MapPin } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { SearchSelect } from "@/components/SearchSelect";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UpgradeGate } from "@/components/UpgradeGate";
import { useBilling } from "@/context/BillingContext";
import { useAnalyst } from "@/context/AnalystContext";
import api from "@/lib/api";
import { groupStatesByRegion } from "@/lib/geo";

/* ---------------- formatting ---------------- */
const fmtVal = (v, f) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  switch (f) {
    case "currency": return n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${n.toLocaleString()}`;
    case "ppsf": return `$${n.toLocaleString()}`;
    case "days": return `${n.toLocaleString()}`;
    case "pct": return `${n > 0 ? "+" : ""}${n}%`;
    case "score": return `${n}`;
    default: return n.toLocaleString();
  }
};
const fmtFull = (n) => (n == null ? "—" : n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : `$${Number(n).toLocaleString()}`);

const STATUS_COLOR = { Bullish: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10", Neutral: "text-amber-500 border-amber-500/40 bg-amber-500/10", Bearish: "text-rose-500 border-rose-500/40 bg-rose-500/10" };

/* ---------------- small pieces ---------------- */
const SectionHead = ({ n, title, sub }) => (
  <div className="mb-6">
    <div className="flex items-center gap-3">
      <span className="font-mono-data text-[11px] text-accent tabular-nums">{n}</span>
      <h2 className="font-display text-lg md:text-lg font-medium tracking-tight">{title}</h2>
    </div>
    {sub && <p className="text-sm text-muted-foreground mt-1 ml-8 max-w-3xl">{sub}</p>}
  </div>
);

function Delta({ v }) {
  if (v === null || v === undefined) return <span className="font-mono-data text-[10px] text-muted-foreground/50">—</span>;
  const up = v > 0, flat = v === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono-data text-[10px] ${flat ? "text-muted-foreground" : up ? "text-emerald-500" : "text-rose-500"}`}>
      <Icon className="w-3 h-3" />{Math.abs(v)}%
    </span>
  );
}

function Spark({ data, color = "hsl(var(--accent))" }) {
  if (!data || data.length < 2) return <div className="h-8" />;
  const d = data.map((y, i) => ({ i, y }));
  return (
    <ResponsiveContainer width="100%" height={32}>
      <AreaChart data={d} margin={{ top: 2, bottom: 2, left: 0, right: 0 }}>
        <defs><linearGradient id="sp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.4} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
        <Area type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} fill="url(#sp)" dot={false} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function KPICard({ k }) {
  const muted = k.status === "not_tracked" || k.status === "building_history";
  return (
    <div className="rounded-xl border border-border bg-surface p-4 flex flex-col justify-between min-h-[118px]" data-testid={`kpi-${k.key}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground leading-tight">{k.label}</span>
        {k.note && <span title={k.note}><Info className="w-3 h-3 text-muted-foreground/40 shrink-0" /></span>}
      </div>
      {muted ? (
        <div className="mt-2">
          <div className="font-mono-data text-[11px] text-muted-foreground/60">{k.status === "not_tracked" ? "Not tracked" : "Building history"}</div>
        </div>
      ) : (
        <>
          <div className="font-display text-2xl tracking-tight mt-1" data-testid={`kpi-${k.key}-value`}>
            {fmtVal(k.value, k.format)}{k.format === "score" && <span className="text-sm text-muted-foreground">/100</span>}
          </div>
          <div className="flex items-center justify-between gap-2 mt-1">
            <div className="flex items-center gap-2"><Delta v={k.delta_30d} /><span className="font-mono-data text-[9px] text-muted-foreground/50">30d</span></div>
            <div className="w-16">{k.spark?.length > 1 && <Spark data={k.spark} />}</div>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- historical charts ---------------- */
const CHART_METRICS_RE = [["median_price", "Median Price", "currency"], ["count", "Inventory", "int"], ["median_dom", "Median DOM", "days"], ["median_ppsf", "Median $/SQFT", "ppsf"]];
const CHART_METRICS_VEH = [["median_price", "Median Price", "currency"], ["count", "Inventory", "int"], ["median_dom", "Median DOM", "days"], ["median_miles", "Median Mileage", "int"]];

function HistoricalCharts({ history, asset }) {
  const metrics = asset === "veh" ? CHART_METRICS_VEH : CHART_METRICS_RE;
  const [metric, setMetric] = useState(metrics[0][0]);
  const meta = metrics.find((m) => m[0] === metric);
  const data = (history || []).map((r) => ({ date: r.date, v: r.metrics?.[metric] })).filter((d) => d.v != null);
  return (
    <div className="rounded-xl border border-border bg-surface p-5" data-testid="mi-historical">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {metrics.map((m) => (
          <button key={m[0]} onClick={() => setMetric(m[0])} data-testid={`chart-metric-${m[0]}`}
            className={`font-mono-data text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${metric === m[0] ? "border-accent/50 text-accent bg-accent/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {m[1]}
          </button>
        ))}
      </div>
      {data.length < 2 ? (
        <div className="h-[280px] flex flex-col items-center justify-center text-center gap-2">
          <RefreshCw className="w-6 h-6 text-muted-foreground/40" />
          <div className="font-mono-data text-xs text-muted-foreground">Building history — trend charts populate from daily snapshots.</div>
          <div className="text-xs text-muted-foreground/60">{data.length === 1 ? "1 snapshot so far." : "No snapshots yet."}</div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs><linearGradient id="hc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity={0.35} /><stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtVal(v, meta[2])} width={54} />
            <Tooltip contentStyle={{ background: "hsl(var(--surface))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => [fmtVal(v, meta[2]), meta[1]]} />
            <Area type="monotone" dataKey="v" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#hc)" isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

/* ---------------- band table ---------------- */
function BandTable({ bands, asset, filters = {} }) {
  const [open, setOpen] = useState(null);
  const [listings, setListings] = useState({});
  const [sortMode, setSortMode] = useState("price");
  const detailBase = asset === "veh" ? "/vehicles" : "/homes";
  const scopeKey = asset === "veh"
    ? `${filters.make || ""}|${filters.model || ""}`
    : `${filters.state || ""}|${filters.city || ""}|${filters.zip || ""}`;
  // When the selected scope changes, collapse any open drill-down and drop the
  // stale per-band listings cache so it refetches for the new scope.
  useEffect(() => { setOpen(null); setListings({}); }, [scopeKey]);
  const cols = asset === "veh"
    ? [["band", "Price Band"], ["count", "Listings"], ["median_price", "Median", "currency"], ["avg_price", "Average", "currency"], ["median_miles", "Med. Miles"], ["median_dom", "Med. DOM"], ["median_msrp_premium", "MSRP Prem.", "pct"], ["inventory_pct", "Inv %", "pct"], ["reduction_pct", "Reductions %", "pct"]]
    : [["band", "Price Band"], ["count", "Listings"], ["median_price", "Median", "currency"], ["avg_price", "Average", "currency"], ["median_ppsf", "Med. $/SQFT", "ppsf"], ["median_dom", "Med. DOM"], ["inventory_pct", "Inv %", "pct"], ["reduction_pct", "Reductions %", "pct"]];

  const scopeParams = (bandKey) => {
    const p = new URLSearchParams();
    p.set("asset", asset);
    p.set("band", bandKey);
    if (asset === "veh") { if (filters.make) p.set("make", filters.make); if (filters.model) p.set("model", filters.model); }
    else { if (filters.state) p.set("state", filters.state); if (filters.city) p.set("city", filters.city); if (filters.zip) p.set("zip", filters.zip); }
    return p;
  };
  const ckey = (bk, s) => `${scopeKey}:${bk}:${s}`;

  const fetchBand = async (bandKey, s) => {
    const ck = ckey(bandKey, s);
    if (listings[ck]) return;
    setListings((m) => ({ ...m, [ck]: { loading: true } }));
    try {
      const p = scopeParams(bandKey); p.set("sort", s);
      const r = await api.get(`/mi/band-listings?${p.toString()}`);
      setListings((m) => ({ ...m, [ck]: { loading: false, data: r.data } }));
    } catch { setListings((m) => ({ ...m, [ck]: { loading: false, data: null } })); }
  };

  const toggle = (b) => {
    if (open === b.key) { setOpen(null); return; }
    setOpen(b.key);
    fetchBand(b.key, sortMode);
  };
  const changeSort = (b, s) => { setSortMode(s); fetchBand(b.key, s); };

  const specLine = (it) => {
    const s = it.specs || {};
    if (asset === "veh") return [s.year, s.miles != null ? `${Number(s.miles).toLocaleString()} mi` : null, s.dom != null ? `${s.dom}d on market` : null].filter(Boolean).join(" · ");
    return [s.beds != null ? `${s.beds} bd` : null, s.baths != null ? `${s.baths} ba` : null, s.sqft ? `${Number(s.sqft).toLocaleString()} sqft` : null, s.dom != null ? `${s.dom}d on market` : null].filter(Boolean).join(" · ");
  };

  return (
    <div className="rounded-xl border border-border bg-surface overflow-hidden" data-testid="mi-band-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="border-b border-border">
            <th className="w-6" />
            {cols.map((c) => <th key={c[0]} className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground font-normal text-right px-3 py-3 first:text-left">{c[1]}</th>)}
          </tr></thead>
          <tbody>
            {bands.map((b) => {
              const ld = listings[ckey(b.key, sortMode)];
              return (
              <Fragment key={b.key}>
                <tr onClick={() => toggle(b)} data-testid={`band-row-${b.key}`}
                  className="border-b border-border/50 hover:bg-accent/5 cursor-pointer">
                  <td className="pl-3">{open === b.key ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}</td>
                  {cols.map((c, i) => (
                    <td key={c[0]} className={`px-3 py-3 tabular-nums ${i === 0 ? "text-left font-medium" : "text-right font-mono-data text-[13px]"} ${!b.sufficient && i > 0 ? "text-muted-foreground/50" : ""}`}>
                      {i === 0 ? b.band : (b[c[0]] == null ? "—" : fmtVal(b[c[0]], c[2] || "int"))}
                    </td>
                  ))}
                </tr>
                {open === b.key && (
                  <tr className="bg-background/40"><td colSpan={cols.length + 1} className="px-4 sm:px-6 py-4">
                    <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                      {b.sufficient
                        ? `${b.count} verified active listings (${b.inventory_pct}% of inventory). Median ${fmtFull(b.median_price)}${b.median_dom != null ? `, typically ${b.median_dom} days on market` : ""}. ${b.reduction_pct}% show a price reduction.`
                        : `Only ${b.count} verified listing(s) in this band — below the minimum sample of 5, so medians are suppressed to avoid a misleading statistic.`}
                    </p>
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Listings in this band</div>
                      <div className="flex items-center gap-3">
                        <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid={`band-sort-${b.key}`}>
                          <button onClick={(e) => { e.stopPropagation(); changeSort(b, "price"); }} data-testid={`band-sort-price-${b.key}`}
                            className={`px-2.5 py-1 text-[11px] font-mono-data transition-colors ${sortMode === "price" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/10"}`}>Price</button>
                          <button onClick={(e) => { e.stopPropagation(); changeSort(b, "value"); }} data-testid={`band-sort-value-${b.key}`}
                            className={`px-2.5 py-1 text-[11px] font-mono-data transition-colors ${sortMode === "value" ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/10"}`}>Best Value</button>
                        </div>
                        {ld?.data?.fair_value_basis && <div className="font-mono-data text-[10px] text-muted-foreground/70">Est. fair value: {ld.data.fair_value_basis}</div>}
                      </div>
                    </div>
                    {ld?.loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid={`band-listings-loading-${b.key}`}><Loader2 className="w-4 h-4 animate-spin" /> Loading listings…</div>}
                    {ld && !ld.loading && (!ld.data || ld.data.count === 0) && <div className="text-sm text-muted-foreground py-3">No verified listings in this band for the current selection.</div>}
                    {ld?.data?.listings?.length > 0 && (
                      <>
                      <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border divide-y divide-border/60" data-testid={`band-listings-${b.key}`}>
                        {ld.data.listings.map((it) => (
                          <Link key={it.id} to={`${detailBase}/${it.id}`} data-testid={`band-listing-${it.id}`}
                            className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-accent/5 transition-colors">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">{it.title || "Listing"}</div>
                              <div className="text-xs text-muted-foreground truncate">{[it.subtitle, specLine(it)].filter(Boolean).join(" — ")}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="font-mono-data text-[13px] text-foreground tabular-nums">{fmtFull(it.price)}</div>
                              {it.fair_value != null && (
                                <div className="text-[11px] text-muted-foreground tabular-nums flex items-center justify-end gap-1">
                                  {it.fair_value_meta ? (
                                    <TooltipProvider delayDuration={100}>
                                      <UITooltip>
                                        <TooltipTrigger asChild onClick={(e) => e.preventDefault()}>
                                          <span className="inline-flex items-center gap-1 cursor-help border-b border-dotted border-muted-foreground/40" data-testid={`fair-value-tip-${it.id}`}>
                                            Fair {fmtFull(it.fair_value)}
                                            <Info className="w-3 h-3 opacity-50" />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="max-w-[240px] text-left">
                                          <div className="font-medium">{it.fair_value_meta.method}</div>
                                          {it.fair_value_meta.detail && <div className="mt-1 opacity-80">{it.fair_value_meta.detail}</div>}
                                        </TooltipContent>
                                      </UITooltip>
                                    </TooltipProvider>
                                  ) : (
                                    <span>Fair {fmtFull(it.fair_value)}</span>
                                  )}
                                  {it.fair_value_delta_pct != null && (
                                    <span className={`font-mono-data ${it.fair_value_delta_pct <= 0 ? "text-emerald-500" : "text-rose-500"}`} data-testid={`band-listing-delta-${it.id}`}>
                                      {it.fair_value_delta_pct > 0 ? "+" : ""}{it.fair_value_delta_pct}%
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                      {ld.data.truncated && <div className="text-[11px] text-muted-foreground/70 mt-2" data-testid={`band-listings-truncated-${b.key}`}>Showing the {ld.data.count.toLocaleString()} highest-priced of {ld.data.total.toLocaleString()} listings in this band. Refine by state / city / ZIP above to see the rest.</div>}
                      </>
                    )}
                  </td></tr>
                )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- rankings ---------------- */
function Rankings({ rankings, asset }) {
  const entries = Object.entries(rankings || {}).filter(([, r]) => r.items?.length);
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mi-rankings">
      {entries.map(([key, r]) => (
        <div key={key} className="rounded-xl border border-border bg-surface p-4" data-testid={`ranking-${key}`}>
          <div className="font-mono-data text-[10px] uppercase tracking-wider text-accent mb-3">{r.label}</div>
          <div className="space-y-2">
            {r.items.slice(0, 5).map((it, i) => (
              <div key={it.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 truncate">
                  <span className="font-mono-data text-[10px] text-muted-foreground/50 w-3">{i + 1}</span>
                  {typeof it.health === "number" && (
                    <span
                      title={`Health ${it.health}`}
                      data-testid={`ranking-health-dot-${key}-${i}`}
                      className={`shrink-0 h-1.5 w-1.5 rounded-full ${it.health >= 66 ? "bg-emerald-500" : it.health < 40 ? "bg-rose-500" : "bg-amber-500"}`}
                    />
                  )}
                  <span className="truncate">{it.key}</span>
                </span>
                <span className="font-mono-data text-[12px] text-muted-foreground shrink-0">
                  {r.field === "median_ppsf" ? `$${it[r.field]?.toLocaleString()}` : r.field === "median_price" ? fmtFull(it[r.field]) : r.field === "value_vs_peers" || r.field === "reduction_pct" || r.field === "median_msrp_premium" ? `${it[r.field] > 0 ? "+" : ""}${it[r.field]}%` : r.field === "median_dom" ? `${it[r.field]}d` : it[r.field]?.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- forecast ---------------- */
function Forecast({ forecast }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid="mi-forecast">
      {forecast.map((f, i) => (
        <div key={i} className="rounded-xl border border-border bg-surface p-5" data-testid={`forecast-${i}`}>
          <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">{f.horizon}</div>
          {f.status !== "ok" ? (
            <p className="text-sm text-muted-foreground/70">{f.note}</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className={`font-display text-xl ${f.direction === "Rising" ? "text-emerald-500" : f.direction === "Falling" ? "text-rose-500" : "text-muted-foreground"}`}>{f.direction}</span>
                <span className="font-mono-data text-xs text-muted-foreground">{f.confidence}% conf.</span>
              </div>
              <div className="h-1.5 rounded-full bg-border overflow-hidden mb-3"><div className="h-full bg-accent rounded-full" style={{ width: `${f.confidence}%` }} /></div>
              <div className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Drivers</div>
              <ul className="text-xs text-muted-foreground space-y-0.5 mb-2">{f.drivers?.map((d, j) => <li key={j}>• {d}</li>)}</ul>
              <div className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground mb-1">Risks</div>
              <ul className="text-xs text-muted-foreground/70 space-y-0.5">{f.risks?.map((d, j) => <li key={j}>• {d}</li>)}</ul>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------------- filter select ---------------- */
function FilterSelect({ label, value, onChange, options, allLabel, testid, disabled, grouped, sortAlpha }) {
  const groups = grouped
    ? groupStatesByRegion(options).map((g) => ({ label: g.region, options: g.options.map((o) => ({ value: o.value, count: o.count })) }))
    : null;
  const base = grouped ? null
    : (sortAlpha ? [...options].sort((a, b) => String(a.value).localeCompare(String(b.value), undefined, { numeric: true })) : options);
  const flat = base ? base.map((o) => ({ value: o.value, count: o.count })) : null;
  return (
    <div className="flex flex-col gap-1">
      <span className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground/70">{label}</span>
      <SearchSelect
        testid={testid}
        value={value}
        onChange={(v) => onChange(v)}
        disabled={disabled}
        allOption={{ value: "", label: allLabel }}
        placeholder={allLabel}
        className="w-[160px]"
        groups={groups || undefined}
        options={flat || undefined}
      />
    </div>
  );
}

/* ================= main dashboard ================= */
function Dashboard({ asset, initialFilters }) {
  const { ask } = useAnalyst() || {};
  const [filters, setFilters] = useState(initialFilters || {});
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState(null);
  const [geoOpts, setGeoOpts] = useState({ l1: [], l2: [], l3: [] });
  const [loading, setLoading] = useState(true);
  const [methodOpen, setMethodOpen] = useState(false);

  const qs = useMemo(() => {
    const p = new URLSearchParams({ asset });
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    return p.toString();
  }, [asset, filters]);

  // level-1 geo (states / makes)
  useEffect(() => {
    const url = asset === "veh" ? "/mi/vehicle-geo" : "/mi/geo";
    api.get(url).then((r) => setGeoOpts((g) => ({ ...g, l1: r.data.options }))).catch(() => {});
  }, [asset]);

  // Preload dependent dropdowns when arriving with a scope (e.g. "View in Market Intelligence").
  useEffect(() => {
    const f = initialFilters || {};
    if (asset === "veh") {
      if (f.make) api.get(`/mi/vehicle-geo?make=${encodeURIComponent(f.make)}`).then((r) => setGeoOpts((g) => ({ ...g, l2: r.data.options }))).catch(() => {});
    } else {
      if (f.state) api.get(`/mi/geo?state=${encodeURIComponent(f.state)}`).then((r) => setGeoOpts((g) => ({ ...g, l2: r.data.options }))).catch(() => {});
      if (f.state && f.city) api.get(`/mi/geo?state=${encodeURIComponent(f.state)}&city=${encodeURIComponent(f.city)}`).then((r) => setGeoOpts((g) => ({ ...g, l3: r.data.options }))).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dashUrl = asset === "veh" ? "/mi/vehicles" : "/mi/real-estate";
  const needsState = asset !== "veh" && !filters.state;
  const needsMake = asset === "veh" && !filters.make;
  const needsScope = needsState || needsMake;
  const load = useCallback(() => {
    if ((asset !== "veh" && !filters.state) || (asset === "veh" && !filters.make)) {
      // Aggregating the entire corpus at once is noisy/misleading — require a scope first
      // (a state for real estate, a make for vehicles).
      setData(null); setSummary(null); setInsights([]); setLoading(false);
      return;
    }
    setLoading(true);
    api.get(`${dashUrl}?${qs}`, { timeout: 30000 }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    setSummary(null); setInsights(null);
    api.get(`/mi/summary?${qs}`, { timeout: 60000 }).then((r) => setSummary(r.data)).catch(() => {});
    api.get(`/mi/insights?${qs}`, { timeout: 60000 }).then((r) => setInsights(r.data.insights || [])).catch(() => setInsights([]));
  }, [dashUrl, qs, asset, filters.state, filters.make]);
  useEffect(() => { load(); }, [load]);

  // dependent geo dropdowns
  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const onL1 = (v) => {
    if (asset === "veh") { setF({ make: v, model: "" }); if (v) api.get(`/mi/vehicle-geo?make=${encodeURIComponent(v)}`).then((r) => setGeoOpts((g) => ({ ...g, l2: r.data.options }))); }
    else { setF({ state: v, city: "", zip: "" }); if (v) api.get(`/mi/geo?state=${encodeURIComponent(v)}`).then((r) => setGeoOpts((g) => ({ ...g, l2: r.data.options }))); }
  };
  const onL2 = (v) => {
    if (asset === "veh") setF({ model: v });
    else { setF({ city: v, zip: "" }); if (v && filters.state) api.get(`/mi/geo?state=${encodeURIComponent(filters.state)}&city=${encodeURIComponent(v)}`).then((r) => setGeoOpts((g) => ({ ...g, l3: r.data.options }))); }
  };

  const exportCsv = () => {
    if (!data?.kpis) return;
    const rows = [["Metric", "Value", "Status"], ...data.kpis.map((k) => [k.label, k.value ?? "", k.status])];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `vaelith-mi-${asset}-${Date.now()}.csv`; a.click();
  };

  const health = summary?.health_score;
  return (
    <div className="space-y-14">
      {/* GLOBAL FILTER BAR */}
      <div className="sticky top-16 z-30 -mx-5 sm:-mx-8 px-5 sm:px-8 py-3 bg-background/85 backdrop-blur-xl border-b border-border" data-testid="mi-filter-bar">
        <div className="flex items-end gap-3 overflow-x-auto pb-1">
          {asset === "veh" ? (
            <>
              <FilterSelect label="Make" value={filters.make} onChange={onL1} options={geoOpts.l1} allLabel="All makes" testid="filter-make" sortAlpha />
              <FilterSelect label="Model" value={filters.model} onChange={onL2} options={geoOpts.l2} allLabel="All models" testid="filter-model" sortAlpha />
              <FilterSelect label="Price Band" value={filters.band} onChange={(v) => setF({ band: v })} options={[{ value: "v1" }, { value: "v2" }, { value: "v3" }, { value: "v4" }, { value: "v5" }].map((b, i) => ({ value: ["$150K–$300K", "$300K–$500K", "$500K–$1M", "$1M–$3M", "$3M+"][i], count: null, _k: ["v1", "v2", "v3", "v4", "v5"][i] }))} allLabel="All bands" testid="filter-band" />
            </>
          ) : (
            <>
              <FilterSelect label="State" value={filters.state} onChange={onL1} options={geoOpts.l1} allLabel="All states" testid="filter-state" grouped />
              <FilterSelect label="City" value={filters.city} onChange={onL2} options={geoOpts.l2} allLabel={filters.state ? "All cities" : "Select a state first"} testid="filter-city" disabled={!filters.state} sortAlpha />
              <FilterSelect label="ZIP" value={filters.zip} onChange={(v) => setF({ zip: v })} options={geoOpts.l3} allLabel={filters.city ? "All ZIPs" : "Select a city first"} testid="filter-zip" disabled={!filters.city} sortAlpha />
            </>
          )}
          <button onClick={exportCsv} data-testid="mi-export" className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-accent/40 transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
        </div>
      </div>

      {needsScope ? (
        <div className="py-24 text-center max-w-xl mx-auto" data-testid={asset === "veh" ? "mi-select-make-prompt" : "mi-select-state-prompt"}>
          {asset === "veh"
            ? <Car className="w-10 h-10 text-accent/70 mx-auto mb-5" strokeWidth={1.5} />
            : <MapPin className="w-10 h-10 text-accent/70 mx-auto mb-5" strokeWidth={1.5} />}
          <h3 className="font-display text-2xl tracking-tight mb-3">{asset === "veh" ? "Select a make to begin" : "Select a state to begin"}</h3>
          <p className="text-muted-foreground leading-relaxed">
            {asset === "veh"
              ? <>Vaelith tracks verified luxury inventory across dozens of marques. A single view of <span className="text-foreground font-medium">all vehicles</span> at once would be noisy and misleading, so choose a <span className="text-foreground font-medium">make</span> (and optionally a model) above to see accurate, grounded market intelligence.</>
              : <>Vaelith tracks verified luxury inventory across 30+ states — not the entire country yet. A national view would be incomplete and misleading, so choose a <span className="text-foreground font-medium">state</span> above to see accurate, grounded market intelligence.</>}
          </p>
        </div>
      ) : loading && !data ? (
        <div className="flex items-center justify-center gap-2 py-24 font-mono-data text-sm text-muted-foreground animate-pulse"><Loader2 className="w-5 h-5 animate-spin" /> Computing verified analytics…</div>
      ) : !data ? (
        <div className="py-24 text-center text-muted-foreground">Verified data unavailable for this scope.</div>
      ) : (
        <>
          {/* AI EXECUTIVE SUMMARY */}
          <section data-testid="mi-executive-summary">
            <SectionHead n="01" title="AI Executive Summary" sub="A grounded read on current market condition — derived only from the verified figures below." />
            <div className="rounded-2xl border border-border bg-surface p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  {summary?.status && <span className={`font-mono-data text-[11px] uppercase tracking-wider border rounded-full px-3 py-1 ${STATUS_COLOR[summary.status]}`} data-testid="mi-market-status">{summary.status}</span>}
                  <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Sample: {data.sample_size?.toLocaleString()} verified listings</span>
                </div>
                <p className="text-base md:text-lg leading-relaxed text-foreground/90" data-testid="mi-summary-text">{summary?.paragraph || "Generating grounded summary…"}</p>
                <button onClick={() => ask?.(`Give me a deeper analysis of the ${data.scope.title} market based on the current verified statistics.`)} data-testid="mi-summary-ask"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/5 px-4 py-2 text-sm text-accent hover:bg-accent/10 transition-colors">
                  <Sparkles className="w-4 h-4" /> Ask the AI to go deeper
                </button>
              </div>
              <div className="flex md:flex-col items-center gap-6 md:border-l md:border-border md:pl-8">
                <div className="text-center">
                  <div className="font-display text-4xl tracking-tight" data-testid="mi-health-score">{health ?? "—"}<span className="text-lg text-muted-foreground">/100</span></div>
                  <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Health Score</div>
                </div>
                <div className="text-center">
                  <div className="font-display text-2xl tracking-tight text-accent">{summary?.confidence ?? "—"}%</div>
                  <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Confidence</div>
                </div>
              </div>
            </div>
          </section>

          {/* MARKET HEALTH DASHBOARD */}
          <section data-testid="mi-kpi-dashboard">
            <SectionHead n="02" title="Market Health Dashboard" sub="Point-in-time KPIs from verified active inventory. Trend deltas & sparklines build from daily snapshots." />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {data.kpis.map((k) => <KPICard key={k.key} k={k} />)}
            </div>
          </section>

          {/* HISTORICAL CHARTS */}
          <section data-testid="mi-charts-section">
            <SectionHead n="03" title="Historical Trends" sub="Rolling time series computed from daily verified snapshots." />
            <HistoricalCharts history={data.history} asset={asset} />
          </section>

          {/* PRICE BAND ANALYTICS */}
          <section data-testid="mi-bands-section">
            <SectionHead n="04" title="Price Band Analytics" sub="Segmented by national luxury price bands. Bands with fewer than 5 verified listings suppress medians (min-sample rule). Click a row to expand." />
            <BandTable bands={data.bands} asset={asset} filters={filters} />
            <p className="text-[11px] text-muted-foreground/60 mt-2">Est. fair value is a per-listing estimate from verified comparables — homes: the cohort's median $/sq ft × <em>this</em> home's sq ft; vehicles: the median price of the same make+model (n≥5), adjusted for <em>this</em> vehicle's mileage. When there aren't enough verified comparables (or a home's sq ft is missing), no estimate is shown rather than a misleading one. Click any listing to open its full detail page.</p>
          </section>

          {/* RANKINGS */}
          <section data-testid="mi-rankings-section">
            <SectionHead n="05" title={asset === "veh" ? "Make & Model Rankings" : "Regional Rankings"} sub="Ranked across the full verified corpus (minimum 5 listings per group)." />
            <Rankings rankings={data.rankings} asset={asset} />
            {asset === "veh" && data.model_rankings && (
              <div className="mt-4"><Rankings rankings={data.model_rankings} asset={asset} /></div>
            )}
          </section>

          {/* AI INSIGHTS */}
          <section data-testid="mi-insights-section">
            <SectionHead n="06" title="AI Insights" sub="Institutional observations — each references a specific verified statistic above." />
            <div className="grid md:grid-cols-2 gap-4">
              {(insights === null) ? <div className="font-mono-data text-sm text-muted-foreground animate-pulse col-span-2 py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Deriving insights…</div>
                : insights.length === 0 ? <p className="text-sm text-muted-foreground col-span-2">Insufficient verified data to derive insights for this scope.</p>
                  : insights.map((ins, i) => (
                    <div key={i} className="rounded-xl border border-border bg-surface p-4 flex gap-3" data-testid={`mi-insight-${i}`}>
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                      <p className="text-sm leading-relaxed">{ins}</p>
                    </div>
                  ))}
            </div>
          </section>

          {/* FORECAST */}
          <section data-testid="mi-forecast-section">
            <SectionHead n="07" title="Forecast" sub="Trend-based projections from verified snapshots. Confidence decreases with horizon; no sold data is used." />
            <Forecast forecast={data.forecast} />
          </section>

          {/* METHODOLOGY */}
          <section data-testid="mi-methodology">
            <button onClick={() => setMethodOpen(!methodOpen)} className="flex items-center gap-2 w-full text-left" data-testid="mi-methodology-toggle">
              <SectionHead n="08" title="Methodology" />
              <ChevronDown className={`w-4 h-4 text-muted-foreground ml-auto transition-transform ${methodOpen ? "rotate-180" : ""}`} />
            </button>
            {methodOpen && (
              <div className="rounded-xl border border-border bg-surface p-6 grid md:grid-cols-2 gap-6 text-sm text-muted-foreground leading-relaxed">
                <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-foreground mb-1">Normalization</div>Median-first statistics. Averages are winsorized (5th–95th percentile) so a single outlier can't distort them. Duplicate listings are removed by {asset === "veh" ? "VIN" : "listing ID"}; non-positive prices and impossible {asset === "veh" ? "mileage" : "square footage"} are excluded.</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-foreground mb-1">Minimum Sample</div>Any geography or band with fewer than 5 verified listings suppresses its medians and shows the underlying count instead of a potentially misleading statistic.</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-foreground mb-1">Trends & Forecast</div>Rolling 30 / 90 / 365-day windows are computed from append-only daily snapshots. Forecasts extrapolate the observed slope; confidence scales with snapshot count and shrinks over longer horizons.</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-foreground mb-1">Health & Data Freshness</div>The Market Health Score is a transparent composite of median days-on-market and price-reduction share. Metrics requiring sold/MLS-status data (sale discount, months of supply, pending %) are marked "Not tracked". All figures recompute after each daily refresh.</div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* ================= page ================= */
export default function Market() {
  const { ent } = useBilling();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "vehicles" ? "veh" : "re");
  // Read any scope passed in the URL once (e.g. from a listing's "View in Market Intelligence").
  const initial = useMemo(() => {
    const asset = params.get("tab") === "vehicles" ? "veh" : "re";
    const f = {};
    if (asset === "veh") {
      if (params.get("make")) f.make = params.get("make");
      if (params.get("model")) f.model = params.get("model");
    } else {
      ["state", "city", "zip"].forEach((k) => { if (params.get(k)) f[k] = params.get(k); });
    }
    return { asset, filters: f };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAsset = (a) => { setTab(a); setParams(a === "veh" ? { tab: "vehicles" } : {}, { replace: true }); };

  if (ent && !ent.market) return <div className="max-w-3xl mx-auto px-5 py-20"><UpgradeGate title="Market Intelligence" note="Market Intelligence is included with the Vaelith Membership." /></div>;

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-10">
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-accent mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Market Intelligence</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">Market Intelligence</h1>
          <p className="text-muted-foreground text-sm mt-3 max-w-2xl leading-relaxed">AI-powered market analytics derived from verified listing data. All statistics are normalized using robust statistical methods designed specifically for luxury markets.</p>
        </div>
        <div className="flex gap-6 shrink-0">
          <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Last Updated</div><div className="font-display text-sm mt-1">Today</div></div>
          <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Coverage</div><div className="font-display text-sm mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />Verified Listings</div></div>
          <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Status</div><div className="font-display text-sm mt-1 text-emerald-500">Live</div></div>
        </div>
      </div>

      {/* ASSET TABS */}
      <div className="flex gap-2 mb-6">
        {[["re", "Real Estate", Building2], ["veh", "Vehicles", Car]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setAsset(k)} data-testid={`mi-tab-${k}`}
            className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm transition-colors ${tab === k ? "bg-foreground text-background" : "border border-border text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      <Dashboard key={tab} asset={tab} initialFilters={tab === initial.asset ? initial.filters : undefined} />
    </div>
  );
}
